// Globals
import { TFunction } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

// Components
import { StyledBadge, StyledSkuLink } from './ProductsListStyles';
import ProductsTableActions from '../ProductsTableActions/ProductsTableActions';
import { Checkbox } from '@/ui';

// Models
import { IProduct } from '@/models/products.model';

// Helpers
import { useIdsManager } from '@/context';
import { WEBSITE_PRODUCT_URL } from '../../helpers';

type TGetProductsTableColumns = {
  t: TFunction;
  setProductForDelete: (product: IProduct) => void;
  handleToggleActive: (product: IProduct) => void;
  canManageProduct: boolean;
};

export const useGetProductsTableColumns = ({
  t,
  setProductForDelete,
  handleToggleActive,
  canManageProduct,
}: TGetProductsTableColumns): ColumnsType<IProduct> => {
  const { ids, removeIds, addIds, selectedAll } = useIdsManager();

  const onItemSelect = (e: CheckboxChangeEvent, id: string) => {
    if (e.target.checked && !selectedAll) {
      addIds([id]);
    } else {
      removeIds([id]);
    }
  };

  return [
    ...(canManageProduct
      ? [
          {
            title: t('products_list_select'),
            key: 'select',
            className: 'checkbox-column',
            render: (_: unknown, record: IProduct) => (
              <Checkbox
                checked={ids?.includes(record.id) || selectedAll}
                onChange={(e) => onItemSelect(e, record?.id)}
              />
            ),
          },
        ]
      : []),
    {
      title: t('ID'),
      dataIndex: 'id',
      className: 'id-column',
      key: 'id',
      render: (name) => name,
    },
    {
      title: t('products_list_title'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => name,
    },
    {
      title: t('products_list_store'),
      dataIndex: 'store',
      key: 'store',
      render: (store) => store,
    },
    {
      title: t('products_list_sku'),
      dataIndex: 'sku',
      key: 'sku',
      render: (sku, record) => {
        const isLinkActive = record.active && record.slug;

        if (!isLinkActive) {
          return sku;
        }

        return (
          <StyledSkuLink href={`${WEBSITE_PRODUCT_URL}/${record.slug}`} target="_blank" rel="noreferrer">
            {sku}
          </StyledSkuLink>
        );
      },
    },
    {
      title: t('products_list_partner_sku'),
      dataIndex: 'external_id',
      key: 'external_id',
      render: (external_id) => external_id ?? '-',
    },
    {
      title: t('products_list_category'),
      dataIndex: 'category',
      key: 'category',
      render: (category) => category,
    },
    {
      title: t('products_list_status'),
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <StyledBadge $isActive={active}>{active ? t('products_list_active') : t('products_list_inactive')}</StyledBadge>
      ),
    },
    {
      // Always shown: view-only roles get just the "open card" entry; ProductsTableActions
      // hides the write actions (delete / publish) when canManage is false.
      title: t('products_list_actions'),
      key: 'actions',
      render: (_: unknown, record: IProduct) => (
        <ProductsTableActions
          record={record}
          setProductForDelete={setProductForDelete}
          handleToggleActive={handleToggleActive}
          canManage={canManageProduct}
        />
      ),
    },
  ];
};
